import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderKanban, Plus, Pencil, Trash2, ArrowRight, Loader2, X } from 'lucide-react'
import { apiClient } from '@/lib/api'

function formatDate(value?: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [busyProjectId, setBusyProjectId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create')
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [nameInput, setNameInput] = useState('')
  const [descriptionInput, setDescriptionInput] = useState('')

  const {
    data: projectsResponse,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.getSpaces(200),
    staleTime: 30 * 1000,
  })

  const { data: conversationsResponse } = useQuery({
    queryKey: ['conversations', '__all__', 'project-counts'],
    queryFn: () => apiClient.getConversations(100),
    staleTime: 30 * 1000,
  })

  const projects = useMemo(() => {
    const items = Array.isArray(projectsResponse?.items) ? [...projectsResponse.items] : []
    items.sort((left, right) => {
      const leftTs = Date.parse(String(left.updated_at || left.created_at || ''))
      const rightTs = Date.parse(String(right.updated_at || right.created_at || ''))
      return (Number.isFinite(rightTs) ? rightTs : 0) - (Number.isFinite(leftTs) ? leftTs : 0)
    })
    return items
  }, [projectsResponse?.items])

  const conversationCountByProject = useMemo(() => {
    const map = new Map<string, number>()
    const items = Array.isArray(conversationsResponse?.items) ? conversationsResponse.items : []
    for (const conversation of items) {
      const projectId = String(conversation.space_id || '').trim()
      if (!projectId) continue
      map.set(projectId, (map.get(projectId) || 0) + 1)
    }
    return map
  }, [conversationsResponse?.items])

  const closeEditor = () => {
    setEditorOpen(false)
    setEditingProjectId(null)
    setNameInput('')
    setDescriptionInput('')
  }

  useEffect(() => {
    if (!editorOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEditor()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editorOpen])

  const openCreateEditor = () => {
    setEditorMode('create')
    setEditingProjectId(null)
    setNameInput('')
    setDescriptionInput('')
    setError(null)
    setEditorOpen(true)
  }

  const openEditEditor = (projectId: string, currentName: string, currentDescription?: string | null) => {
    setEditorMode('edit')
    setEditingProjectId(projectId)
    setNameInput(currentName || '')
    setDescriptionInput(currentDescription || '')
    setError(null)
    setEditorOpen(true)
  }

  const handleSubmitEditor = async () => {
    const name = nameInput.trim()
    if (!name) {
      setError('Введите название проекта')
      return
    }
    const description = descriptionInput.trim() || undefined
    const targetBusyId = editorMode === 'create' ? '__new__' : editingProjectId
    if (!targetBusyId) {
      setError('Не удалось определить проект для обновления')
      return
    }
    setError(null)
    try {
      setBusyProjectId(targetBusyId)
      if (editorMode === 'create') {
        await apiClient.createSpace({
          name,
          description,
        })
      } else if (editingProjectId) {
        await apiClient.updateSpace(editingProjectId, {
          name,
          description,
        })
      }
      closeEditor()
      await refetch()
      await queryClient.invalidateQueries({ queryKey: ['spaces'] })
      await queryClient.invalidateQueries({ queryKey: ['conversations'] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить проект')
    } finally {
      setBusyProjectId(null)
    }
  }

  const handleDelete = async (projectId: string, projectName: string, isPersonal?: boolean) => {
    if (isPersonal) {
      setError('Личный проект нельзя удалить')
      return
    }
    if (!window.confirm(`Удалить проект "${projectName}"?`)) return
    setError(null)
    try {
      setBusyProjectId(projectId)
      await apiClient.deleteSpace(projectId)
      await refetch()
      await queryClient.invalidateQueries({ queryKey: ['spaces'] })
      await queryClient.invalidateQueries({ queryKey: ['conversations'] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить проект')
    } finally {
      setBusyProjectId(null)
    }
  }

  const editorBusyId = editorMode === 'create' ? '__new__' : editingProjectId
  const isEditorBusy = Boolean(editorBusyId && busyProjectId === editorBusyId)

  return (
    <div className="projects-page flex flex-col min-h-full">
      <div className="projects-header sticky top-0 z-10 glass-header" style={{ padding: '10px 16px' }}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-bold flex items-center gap-2" style={{ fontSize: '14px' }}>
              <FolderKanban size={16} className="text-telegram-button" />
              Проекты
            </h1>
            <p style={{ fontSize: '10px', color: 'var(--header-hint-color)' }}>
              Пространства для кейсов, чатов и общих материалов
            </p>
          </div>
          <button
            onClick={openCreateEditor}
            disabled={busyProjectId === '__new__'}
            className="rounded-lg glass-icon flex items-center gap-1 px-2 active:scale-95 transition-transform disabled:opacity-50"
            style={{ height: '32px' }}
          >
            {busyProjectId === '__new__' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            <span className="text-[10px] font-medium">Новый</span>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: '12px 16px calc(12px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        {error && (
          <div className="glass-surface text-red-500 text-xs" style={{ padding: '10px 12px', marginBottom: '10px' }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="glass-surface flex items-center justify-center gap-2 text-telegram-hint" style={{ padding: '18px 12px' }}>
            <Loader2 size={16} className="animate-spin" />
            <p className="text-xs">Загрузка проектов…</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="glass-surface flex items-center justify-center gap-2 text-telegram-hint" style={{ padding: '18px 12px' }}>
            <FolderKanban size={16} />
            <p className="text-xs">Проектов пока нет</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {projects.map((project) => {
              const isBusy = busyProjectId === project.id
              const isPersonal = Boolean(project.is_personal)
              return (
                <div key={project.id} className="glass-surface" style={{ padding: '12px' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          type="button"
                          onClick={() => navigate(`/projects/${encodeURIComponent(project.id)}`)}
                          className="text-sm font-semibold truncate text-left hover:underline active:opacity-70"
                          title="Открыть проект"
                        >
                          {project.name}
                        </button>
                        <span
                          className="rounded-full px-2 py-0.5 text-[9px]"
                          style={{
                            background: isPersonal ? 'rgba(127,127,127,0.2)' : 'rgba(36,129,204,0.15)',
                            color: isPersonal ? 'var(--tg-theme-hint-color, #999)' : 'var(--tg-theme-button-color, #2481cc)',
                          }}
                        >
                          {isPersonal ? 'Личный' : 'Проект'}
                        </span>
                      </div>
                      {project.description ? (
                        <p className="text-[11px] text-telegram-hint" style={{ marginBottom: '4px' }}>{project.description}</p>
                      ) : (
                        <p className="text-[11px] text-telegram-hint" style={{ marginBottom: '4px' }}>Без описания</p>
                      )}
                      <p className="text-[10px] text-telegram-hint">
                        Обновлён: {formatDate(String(project.updated_at || project.created_at || ''))}
                      </p>
                      <p className="text-[10px] text-telegram-hint">
                        Чатов: {conversationCountByProject.get(project.id) || 0}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/projects/${encodeURIComponent(project.id)}`)}
                      className="shrink-0 rounded-lg glass-icon flex items-center justify-center active:scale-90 transition-transform"
                      style={{ width: '30px', height: '30px' }}
                      title="Открыть проект"
                    >
                      <ArrowRight size={14} className="text-telegram-button" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEditEditor(project.id, project.name, project.description)}
                      disabled={isBusy}
                      className="rounded-lg px-2 py-1 text-[10px] glass-icon text-telegram-hint active:scale-95 transition-transform disabled:opacity-50"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Pencil size={11} />
                        Изменить
                      </span>
                    </button>
                    {!isPersonal && (
                      <button
                        onClick={() => void handleDelete(project.id, project.name, isPersonal)}
                        disabled={isBusy}
                        className="rounded-lg px-2 py-1 text-[10px] glass-icon text-telegram-hint hover:text-red-500 active:scale-95 transition-transform disabled:opacity-50"
                      >
                        <span className="inline-flex items-center gap-1">
                          {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                          Удалить
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {editorOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          onClick={() => {
            if (!isEditorBusy) closeEditor()
          }}
        >
          <div className="absolute inset-0 bg-black/30 animate-fade-in" />
          <div
            className="relative glass-dropdown rounded-t-3xl glass-appear"
            style={{
              paddingTop: '12px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingBottom: 'calc(16px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 pb-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  {editorMode === 'create' ? 'Создать проект' : 'Изменить проект'}
                </p>
                <p className="text-[11px] text-telegram-hint mt-1">
                  Проект объединяет связанные чаты и общий контекст.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditor}
                disabled={isEditorBusy}
                className="shrink-0 rounded-full flex items-center justify-center disabled:opacity-50"
                style={{ width: '26px', height: '26px', background: 'var(--glass-border)', color: 'var(--dropdown-text-color)' }}
              >
                <X size={13} />
              </button>
            </div>

            <div className="flex flex-col gap-2 py-1">
              <input
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                placeholder="Название проекта"
                className="glass-input text-sm"
                style={{ height: '38px', padding: '0 12px' }}
                maxLength={120}
                disabled={isEditorBusy}
              />
              <textarea
                value={descriptionInput}
                onChange={(event) => setDescriptionInput(event.target.value)}
                placeholder="Описание (опционально)"
                className="glass-input text-sm resize-none"
                style={{ minHeight: '88px', maxHeight: '180px', padding: '10px 12px' }}
                maxLength={1000}
                disabled={isEditorBusy}
              />
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEditor}
                disabled={isEditorBusy}
                className="rounded-xl glass-icon px-3 h-[34px] text-[11px] font-medium text-telegram-hint disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => void handleSubmitEditor()}
                disabled={isEditorBusy || !nameInput.trim()}
                className="rounded-xl bg-telegram-button text-white px-3 h-[34px] text-[11px] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {isEditorBusy && <Loader2 size={12} className="animate-spin" />}
                {editorMode === 'create' ? 'Создать' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
